import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { DependencyIconState } from '../../map_dependencies/components/DualIconContainer';
import { getDependencyId } from '@mm-shared/dependencies';

/**
 * Bulk selector for multiple maps' dependency states
 * Optimized for rendering multiple maps at once
 */
export const selectMultipleMapsDependencyData = createSelector(
  [
    (state: RootState) => state.map3UI.mapControls.relatives.visibleMapsDependencies,
    (state: RootState) => state.map3UI.mapControls.relatives.addedMaps,
    (state: RootState) => state.map3UI.mapControls.relatives.relatedToVisibleMaps,
    (state: RootState, dependencies: any[]) => dependencies,
    (state: RootState, dependencies: any[], mapUuids: string[]) => mapUuids,
  ],
  (dependenciesVisibilityState, addedMaps, relatedToVisibleMaps, dependencies, mapUuids) => {
    return mapUuids.reduce(
      (acc, mapUuid) => {
        // Calculate dependency state
        const mapDependencies = dependencies.filter(
          (dep) => dep.sourceMap.uuid === mapUuid || dep.targetMap.uuid === mapUuid,
        );

        let dependencyState: DependencyIconState = 'no-dependencies';
        if (mapDependencies.length > 0) {
          const visibleCount = mapDependencies.filter((dep) => {
            const depId = getDependencyId(dep);
            return dependenciesVisibilityState.find((d) => d.depId === depId)?.visible || false;
          }).length;

          if (visibleCount === 0) {
            dependencyState = 'none-visible';
          } else if (visibleCount === mapDependencies.length) {
            dependencyState = 'all-visible';
          } else {
            dependencyState = 'mixed';
          }
        }

        // Calculate visibility
        const allMapOptions = [...addedMaps, ...relatedToVisibleMaps];
        const mapOption = allMapOptions.find((m) => m.mapUuid === mapUuid);
        const isVisible = mapOption?.visible || false;

        acc[mapUuid] = {
          dependencyState,
          dependencyCount: mapDependencies.length,
          visibleDependencyCount: mapDependencies.filter((dep) => {
            const depId = getDependencyId(dep);
            return dependenciesVisibilityState.find((d) => d.depId === depId)?.visible || false;
          }).length,
          isVisible,
        };

        return acc;
      },
      {} as Record<
        string,
        {
          dependencyState: DependencyIconState;
          dependencyCount: number;
          visibleDependencyCount: number;
          isVisible: boolean;
        }
      >,
    );
  },
);
